import {
  ConnectionType,
  Flex,
  MixpanelEvent,
  VStack,
  useMarketAccountsContext,
  useMixpanel,
  type Security,
} from '@talos/kyoko';
import { forwardRef, useCallback, useMemo, type ForwardedRef } from 'react';
import { MarketAccountSearchList } from '../../../../components/MarketAccountSelectionList/MarketAccountSearchList';
import { useDeepDiveContext } from '../../../../providers/DeepDiveProvider';

interface MarketsSelectorProps {
  security: Security;
}

export const MarketsSelector = forwardRef(({ security }: MarketsSelectorProps, ref: ForwardedRef<HTMLInputElement>) => {
  const mixpanel = useMixpanel();
  const { marketAccountsList } = useMarketAccountsContext();
  const { selectedMarkets, selectedMarketAccounts, selectedAggregation, setMarketSelection } = useDeepDiveContext();

  const securityMarkets = useMemo(() => security?.Markets || [], [security]);
  const securityMarketAccounts = useMemo(
    () => marketAccountsList?.filter(ma => securityMarkets.includes(ma.Market)).map(ma => ma.Name) || [],
    [marketAccountsList, securityMarkets]
  );

  const handleMarketAccountsChange = useCallback(
    (newSelectedMarketAccounts: string[]) => {
      mixpanel.track(MixpanelEvent.UpdateMarketSelection);
      setMarketSelection({
        markets: selectedMarkets,
        marketAccounts: newSelectedMarketAccounts.toSorted(),
        aggregation: selectedAggregation,
      });
    },
    [mixpanel, setMarketSelection, selectedMarkets, selectedAggregation]
  );

  const handleMarketsChange = useCallback(
    (newSelectedMarkets: string[]) => {
      mixpanel.track(MixpanelEvent.UpdateMarketSelection);
      setMarketSelection({
        markets: newSelectedMarkets.toSorted(),
        marketAccounts: selectedMarketAccounts,
        aggregation: selectedAggregation,
      });
    },
    [mixpanel, setMarketSelection, selectedMarketAccounts, selectedAggregation]
  );

  return (
    <Flex h="100%" flex="1" minHeight="250px">
      <VStack w="100%" flex="1" alignItems="flex-start" justifyContent="flex-start">
        <MarketAccountSearchList
          ref={ref}
          availableMarkets={securityMarkets}
          availableMarketAccounts={securityMarketAccounts}
          selectedMarketAccounts={selectedMarketAccounts}
          selectedMarkets={selectedMarkets}
          security={security}
          scrollContent={true}
          onChangeMarketAccounts={handleMarketAccountsChange}
          onChangeMarkets={handleMarketsChange}
          connectionType={ConnectionType.MarketData}
          showInactiveMarketAccounts={false}
        />
      </VStack>
    </Flex>
  );
});
